export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_MAX_AGE_DAYS = 182;

export type CookieConsentPreferences = {
  version: number;
  necessary: true;
  analytics: boolean;
  updatedAt: string;
};

const COOKIE_NAME = "senta_cookie_consent";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * COOKIE_CONSENT_MAX_AGE_DAYS;

export function useCookieConsent() {
  const consent = useCookie<CookieConsentPreferences | null>(COOKIE_NAME, {
    default: () => null,
    maxAge: COOKIE_MAX_AGE_SECONDS,
    path: "/",
    sameSite: "lax",
    secure: import.meta.env.PROD,
  });

  const bannerOpen = useState("cookie-consent-banner-open", () => false);
  const preferencesOpen = useState(
    "cookie-consent-preferences-open",
    () => false,
  );

  const hasValidConsent = computed(
    () =>
      consent.value?.version === COOKIE_CONSENT_VERSION &&
      consent.value?.necessary === true &&
      typeof consent.value?.analytics === "boolean",
  );

  const analyticsAccepted = computed(
    () => hasValidConsent.value && consent.value?.analytics === true,
  );

  function initialiseCookieConsent() {
    if (!import.meta.client) return;

    bannerOpen.value = !hasValidConsent.value;
  }

  function writeConsent(analytics: boolean) {
    consent.value = {
      version: COOKIE_CONSENT_VERSION,
      necessary: true,
      analytics,
      updatedAt: new Date().toISOString(),
    };

    bannerOpen.value = false;
    preferencesOpen.value = false;
  }

  function acceptAllCookies() {
    writeConsent(true);
  }

  function rejectOptionalCookies() {
    writeConsent(false);
  }

  function saveCookiePreferences(analytics: boolean) {
    writeConsent(analytics);
  }

  function openCookiePreferences() {
    bannerOpen.value = false;
    preferencesOpen.value = true;
  }

  function closeCookiePreferences() {
    preferencesOpen.value = false;

    if (!hasValidConsent.value) {
      bannerOpen.value = true;
    }
  }

  return {
    analyticsAccepted,
    bannerOpen,
    consent,
    hasValidConsent,
    preferencesOpen,
    acceptAllCookies,
    closeCookiePreferences,
    initialiseCookieConsent,
    openCookiePreferences,
    rejectOptionalCookies,
    saveCookiePreferences,
  };
}
